import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnReorderEvent,
  GridColumnResizeEvent,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridNoRecords,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
  GridSortChangeEvent,
  GridToolbar
} from '@progress/kendo-react-grid';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LinkedCell } from './customCells/LinkedCell';
import DateCell from './customCells/DateCell';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  FilterOperator,
  SortDescriptor
} from '@progress/kendo-data-query';
import {
  CONTENT_TYPE_FIELD,
  DEFAULT_FACETS_COUNT,
  DEFAULT_ROWS_PER_PAGE,
  DOC_ID_FIELD,
  EMPTY_VALUE,
  EXTENSION_FIELD,
  LOCKED_FIELD,
  PERMISSIONS_FIELD
} from '../../config/consts';
import GearLoader from '../common/GearLoader';
import { ColumnMenu } from './columnMenu/ColumnMenu';
import { GEAR_FIELD_TYPE, GearFieldType, GearFieldTypeToFilterType } from '../../global/enums/GearFieldType';
import { ElementDimensions, useWindowDimensions } from '../../global/hooks/useWindowDimensions';
import { GearFacetValue } from '../../Model/GearFacets';
import { ExtensionCell } from './customCells/ExtensionCell';
import { useViewsContext } from '../../context/view/ViewsContext';
import { useSearchContext } from '../../context/search/SearchContext';
import { GearSearchQueryRequest } from '../../Model/GearSearchQueryRequest';
import { GearDocument } from '../../Model/GearDocument';
import { GearToolbar } from './toolbar/GearToolbar';
import { GearFilterRequest } from '../../Model/GearFilterRequest';
import { useDocumentsContext } from '../../context/documents/DocumentsContext';
import { useInternationalization } from '@progress/kendo-react-intl';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { EditableNumericTextBoxCell } from './customCells/EditableNumericTextBoxCell';
import { GearGridCell } from './customCells/GearGridCell';
import DateTimeCell from './customCells/DateTimeCell';
import { useCurrentUserContext } from '../../context/currentUser/CurrentUserContext';
import { BooleanCell } from './customCells/BooleanCell';
import { GearFieldForView } from '../../Model/GearFieldForView';
import { DocumentsGridHelper } from '../../helpers/DocumentsGridHelper';
import { LockCell } from './customCells/LockedCell';
import dayjs from 'dayjs';
import { MultiChoiceCell } from './customCells/MultiChoiceCell';
import { GridPager } from './pager/GridPager';
import { HeaderCell } from './customCells/HeaderCell';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import { ActionsCell } from './customCells/ActionsCell';
import { GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT } from '../../Model/enums/GearUserAllowedActions';
import { useGlobalizationContext } from '../../context/globalization/GlobalizationContext';
import { useGetView } from '../../context/view/useGetView';
import { useGetAllFields } from '../../context/fields/useGetAllFields';
import { useTranslation } from '../../context/globalization/useTranslation';
import { FilterActionsHandle } from './toolbar/FilterActions';
import { LinkCell } from './customCells/LinkCell';
import { areQueryFiltersEqual, getDateTimeRangeFilters } from './helpers/gearGridHelpers';
import { getGearDateTimeFilterFormat } from '../../helpers/DayJsHelper';

type GearGridCommonProperties = {
  [DOC_ID_FIELD]: string;
  [CONTENT_TYPE_FIELD]: string;
  [EXTENSION_FIELD]: string;
  [PERMISSIONS_FIELD]: number[];
};

type GearGridDocument = GearGridCommonProperties & GearDocument;

const GearGrid: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const filterActionsHandleRef = useRef(null) as React.RefObject<FilterActionsHandle | null>;
  const { data, isLoading, queryRequest, setQueryRequest, compositeFilterDescriptor, setCompositeFilterDescriptor } =
    useSearchContext();
  const { setSelectedDocuments, openDocument, openDocumentLoading } = useDocumentsContext();
  const { selectedIndex } = useCurrentUserContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const [gridData, setGridData] = useState<GearGridDocument[]>([]);
  const [select, setSelect] = useState<SelectDescriptor>();
  const [initialFacets, setInitialFacets] = useState<Record<string, GearFacetValue[]>>();
  const [columnChanges, setColumnChanges] = useState<{
    widths: Record<string, number>;
    order: string[];
  }>({ widths: {}, order: [] });
  const [scrollPosition, setScrollPosition] = useState(0);
  const { selectedView } = useViewsContext();
  const { data: currentView, isLoading: currentViewLoading } = useGetView(selectedView, selectedLocale);
  const { data: allFields } = useGetAllFields(selectedLocale, selectedIndex?.id);
  const columns = currentView?.fields;
  const intl = useInternationalization();

  useLayoutEffect(() => {
    if (scrollPosition > 0) {
      const scrollContainer = ref.current?.querySelector('.k-grid-content');
      if (scrollContainer) {
        scrollContainer.scrollLeft = scrollPosition;
        setScrollPosition(0);
      }
    }
  }, [columnChanges.order, scrollPosition]);

  const actionsFacets = useMemo((): GearFacetValue[] => {
    if (!gridData || gridData.length === 0) {
      return [];
    }

    const actionValueToName: Record<number, string> = {};
    for (const key in GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT) {
      const value = GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT[key as keyof typeof GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT];
      actionValueToName[value] = key;
    }

    const actionCounts: Record<string, number> = {};

    gridData.forEach(item => {
      const actions = item[PERMISSIONS_FIELD];
      if (!actions || !actions.length) {
        return;
      }

      const hasNonZeroActions = actions.some(value => value !== 0);

      actions.forEach(actionValue => {
        if (actionValue === 0 && hasNonZeroActions) {
          return;
        }

        const actionName = actionValueToName[actionValue];
        if (actionName) {
          actionCounts[actionName] = (actionCounts[actionName] ?? 0) + 1;
        }
      });
    });

    return Object.entries(actionCounts)
      .filter(([, count]) => count > 0)
      .map(([action, count]) => ({
        value: translate(`grid.filter${action.charAt(0) + action.slice(1).toLowerCase()}`),
        count
      }));
  }, [gridData, translate]);

  useEffect(() => {
    if (!columns || !setQueryRequest || !selectedView || selectedIndex?.id == null) {
      return;
    }

    if (queryRequest !== undefined && queryRequest.viewId === selectedView) {
      return;
    }

    const viewFacets = columns
      .filter(field => field.facetable)
      .map(field => `${field.name},count:${field.facetsCount ?? DEFAULT_FACETS_COUNT}`);

    const facetsForGlobalFilters = (currentView?.globalFilters
      ?.map(globalFilter => {
        const matchingField = allFields?.find(field => field.id === globalFilter.id);
        if (matchingField && matchingField.facetable) {
          return `${matchingField.name},count:${globalFilter.facetsCount ?? DEFAULT_FACETS_COUNT}`;
        }
        return null;
      })
      .filter(Boolean) || []) as string[];

    const facetMap = new Map<string, { facet: string; count: number }>();

    viewFacets.concat(facetsForGlobalFilters).forEach(facet => {
      const [fieldName, countPart] = facet.split(',count:');
      const count = parseInt(countPart, 10);

      const existing = facetMap.get(fieldName);
      if (!existing || existing.count < count) {
        facetMap.set(fieldName, { facet, count });
      }
    });

    const allFacets = Array.from(facetMap.values()).map(item => item.facet);

    const selectColumns = columns
      .map(column => column.name)
      .concat([DOC_ID_FIELD, CONTENT_TYPE_FIELD, EXTENSION_FIELD])
      .filter((value, index, array) => array.indexOf(value) === index);

    const initialQueryRequest: GearSearchQueryRequest = {
      size: DEFAULT_ROWS_PER_PAGE,
      includeTotalCount: true,
      facets: allFacets,
      select: selectColumns,
      indexId: selectedIndex.id,
      viewId: selectedView,
      skip: 0
    };

    setQueryRequest(initialQueryRequest);
  }, [columns, selectedView, selectedIndex?.id, setQueryRequest, queryRequest, currentView?.globalFilters, allFields]);

  useEffect(() => {
    if (!data?.documentsWithPerms) {
      return;
    }

    const result: GearGridDocument[] = data.documentsWithPerms.map(item => ({
      [DOC_ID_FIELD]: item.document[DOC_ID_FIELD] as string,
      [CONTENT_TYPE_FIELD]: item.document[CONTENT_TYPE_FIELD] as string,
      [EXTENSION_FIELD]: item.document[EXTENSION_FIELD] as string,
      ...item.document,
      [PERMISSIONS_FIELD]: DocumentsGridHelper.getActionsFromFlag(item.actions)
    }));
    setGridData(result);

    if (data?.facets && !initialFacets) {
      setInitialFacets(data.facets);
    }
  }, [data?.documentsWithPerms, data?.facets, initialFacets]);

  useEffect(() => {
    if (setSelectedDocuments) {
      setSelectedDocuments([]);
    }
    setSelect({});
    setColumnChanges({ order: [], widths: {} });
    setInitialFacets(undefined);
  }, [selectedIndex?.id, selectedView, setSelectedDocuments]);

  const sort: SortDescriptor[] | undefined = useMemo(() => {
    if (!queryRequest?.orderBy) {
      return undefined;
    }
    return [
      {
        field: queryRequest.orderBy,
        dir: queryRequest.orderByIsDescending ? 'desc' : 'asc'
      }
    ];
  }, [queryRequest?.orderBy, queryRequest?.orderByIsDescending]);

  useEffect(() => {
    if (columns && columns.length > 0) {
      const initialOrder = columns.map(col => col.name);
      setColumnChanges(prev => ({
        ...prev,
        order: initialOrder
      }));
    }
  }, [columns]);

  const sortChange = (descriptors: SortDescriptor[]) => {
    if (queryRequest && setQueryRequest) {
      const newQueryParams: GearSearchQueryRequest = {
        ...queryRequest,
        orderBy: descriptors.length > 0 ? descriptors[0].field : undefined,
        orderByIsDescending: descriptors.length > 0 && descriptors[0].dir === 'desc' ? true : undefined
      };
      setQueryRequest(newQueryParams);
    }
  };

  const buildFiltersFromComposite = React.useCallback(
    (composite: CompositeFilterDescriptor | undefined, cols: typeof columns) => {
      if (!composite || !cols) {
        return { gridFilters: [], actionFilters: [] };
      }

      let actionFilters: number[] = [];
      const gridFilters = composite.filters
        .filter(descriptor => (descriptor as CompositeFilterDescriptor).filters.length > 0)
        .flatMap(descriptor => {
          const newFiltersDescriptors = (descriptor as CompositeFilterDescriptor).filters as FilterDescriptor[];
          const filteredFieldName = newFiltersDescriptors[0].field as string;
          const filteredOperator = newFiltersDescriptors[0].operator as FilterOperator;

          if (filteredFieldName === PERMISSIONS_FIELD) {
            const filteredValues = newFiltersDescriptors
              .filter(i => i.value !== undefined && i.value !== null)
              .map(i => i.value?.toString());

            actionFilters = filteredValues
              .map(actionDisplayName => {
                for (const key in GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT) {
                  const actionKey = key.charAt(0) + key.slice(1).toLowerCase();
                  const translatedValue = translate(`grid.filter${actionKey}`);
                  if (translatedValue === actionDisplayName) {
                    return GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT[key as keyof typeof GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT];
                  }
                }
                return 0;
              })
              .filter(flag => flag !== undefined && flag !== 0);

            return [];
          }

          const filteredFieldType = cols.find(field => field.name === filteredFieldName)?.type || GEAR_FIELD_TYPE.TEXT;

          if (filteredFieldType === GEAR_FIELD_TYPE.DATETIME || filteredFieldType === GEAR_FIELD_TYPE.DATE) {
            const dateValue = newFiltersDescriptors[0].value;
            if (dateValue) {
              if (filteredOperator === FilterOperator.EqualTo) {
                return getDateTimeRangeFilters(new Date(dateValue), filteredFieldName);
              }

              let startOrEndOfDay: dayjs.Dayjs;
              const localDate = dayjs(dateValue);
              if (
                filteredOperator === FilterOperator.LessThanOrEqual ||
                filteredOperator === FilterOperator.GreaterThan
              ) {
                startOrEndOfDay = localDate.endOf('day');
              } else {
                startOrEndOfDay = localDate.startOf('day');
              }
              return [
                {
                  fieldName: filteredFieldName,
                  operator: filteredOperator,
                  values: [getGearDateTimeFilterFormat(startOrEndOfDay.toDate())],
                  fieldType: GearFieldTypeToFilterType(filteredFieldType)
                } as GearFilterRequest
              ];
            }
            return [];
          } else {
            const filteredValues = newFiltersDescriptors
              .filter(i => i.value !== undefined && i.value !== null)
              .map(i => i.value?.toString());

            const newFilter = {
              fieldName: filteredFieldName,
              operator: filteredOperator,
              values: filteredValues,
              fieldType: filteredFieldType
            } as GearFilterRequest;

            return [newFilter];
          }
        });

      return { gridFilters, actionFilters };
    },
    [translate]
  );

  useEffect(() => {
    if (!columns) {
      return;
    }

    setQueryRequest(prev => {
      if (!prev) {
        return prev;
      }

      if (prev.viewId !== selectedView) {
        return prev;
      }

      const globalFilters = prev.filters?.filter(filter => filter.isGlobal) ?? [];

      const { gridFilters, actionFilters } = buildFiltersFromComposite(compositeFilterDescriptor, columns);

      const nextFilters = [...globalFilters, ...gridFilters];
      const nextActionsFilter = actionFilters.length > 0 ? actionFilters : undefined;

      const filtersChanged = !areQueryFiltersEqual(prev.filters, nextFilters);

      const actionsChanged =
        (prev.actionsFilter?.length ?? 0) !== (nextActionsFilter?.length ?? 0) ||
        prev.actionsFilter?.some((v, i) => v !== nextActionsFilter?.[i]);

      if (!filtersChanged && !actionsChanged) {
        return prev;
      }

      return {
        ...prev,
        filters: nextFilters,
        actionsFilter: nextActionsFilter,
        skip: 0
      };
    });
  }, [compositeFilterDescriptor, columns, buildFiltersFromComposite, selectedView, setQueryRequest]);

  const getCellType = (_fieldName: string, _columns?: GearFieldForView[]): GearFieldType => {
    const column = _columns?.find(field => field.name === _fieldName);
    if (!column) {
      return GEAR_FIELD_TYPE.TEXT;
    }
    return column.type;
  };

  const cellRender = React.useCallback(
    (cellProps: GridCellProps) => {
      if (!cellProps.field) {
        return null;
      }
      const isExtensionCell = cellProps.field === EXTENSION_FIELD;
      const isLinkedCell =
        cellProps.field !== LOCKED_FIELD &&
        (columns?.find(field => field.name === cellProps.field && field.isDocumentLink) || false);

      const cellType: GearFieldType = getCellType(cellProps.field, columns);

      if (isExtensionCell) {
        return <ExtensionCell {...cellProps} />;
      } else if (isLinkedCell) {
        const linkedCellField = cellProps.field || EMPTY_VALUE;
        let linkedCellValue = cellProps.dataItem[linkedCellField];
        let linkedCellTitle = linkedCellValue?.toString();
        if (cellType === GEAR_FIELD_TYPE.DATE) {
          linkedCellValue = intl.formatDate(new Date(linkedCellValue));
          linkedCellTitle = intl.formatDate(new Date(linkedCellValue), { date: 'full' });
        } else if (cellType === GEAR_FIELD_TYPE.DATETIME) {
          linkedCellValue = intl.formatDate(new Date(linkedCellValue), { datetime: 'short' });
          linkedCellTitle = intl.formatDate(new Date(linkedCellValue), { date: 'full', datetime: 'medium' });
        } else if (cellType === GEAR_FIELD_TYPE.NUMERIC) {
          linkedCellValue = linkedCellValue?.toString();
          linkedCellTitle = isNaN(linkedCellValue) ? '' : linkedCellValue?.toString();
        }
        return (
          <LinkedCell
            {...cellProps}
            onLinkClicked={() => {
              if (openDocument) {
                const docId = cellProps.dataItem[DOC_ID_FIELD];
                const contentType = cellProps.dataItem[CONTENT_TYPE_FIELD];
                openDocument(contentType, docId);
              }
            }}
            title={linkedCellTitle?.toString()}
            value={linkedCellValue?.toString()}
            documentId={cellProps.dataItem[DOC_ID_FIELD]}
            documentName={linkedCellValue?.toString()}
            contentType={cellProps.dataItem[CONTENT_TYPE_FIELD]}
          />
        );
      } else if (cellType === GEAR_FIELD_TYPE.DATE) {
        return <DateCell {...cellProps} />;
      } else if (cellType === GEAR_FIELD_TYPE.LINK) {
        return <LinkCell {...cellProps} />;
      } else if (cellProps.field === PERMISSIONS_FIELD) {
        return <ActionsCell {...cellProps} />;
      } else if (cellType === GEAR_FIELD_TYPE.MULTICHOICE) {
        return <MultiChoiceCell {...cellProps} />;
      } else if (cellType === GEAR_FIELD_TYPE.DATETIME) {
        return <DateTimeCell {...cellProps} />;
      } else if (
        cellType === GEAR_FIELD_TYPE.NUMERIC ||
        cellType === GEAR_FIELD_TYPE.INT32 ||
        cellType === GEAR_FIELD_TYPE.INT64
      ) {
        return <EditableNumericTextBoxCell {...cellProps} fieldType={cellType} inEdit={false} />;
      } else if (cellType === GEAR_FIELD_TYPE.BOOLEAN) {
        if (cellProps.field === LOCKED_FIELD) {
          const isLinkedLockField = Boolean(
            columns?.find(field => field.name === LOCKED_FIELD && field.isDocumentLink)
          );

          return (
            <LockCell
              {...cellProps}
              linked={isLinkedLockField}
              onLinkClicked={
                isLinkedLockField
                  ? () => {
                      if (openDocument) {
                        const docId = cellProps.dataItem[DOC_ID_FIELD];
                        const contentType = cellProps.dataItem[CONTENT_TYPE_FIELD];
                        openDocument(contentType, docId);
                      }
                    }
                  : undefined
              }
            />
          );
        }
        return <BooleanCell {...cellProps} />;
      }
      return (
        <GearGridCell {...cellProps} title={cellProps.dataItem[cellProps.field] || ''}>
          {cellProps.dataItem[cellProps.field] || ''}
        </GearGridCell>
      );
    },
    [columns, intl, openDocument]
  );

  const pageSizeValue = useMemo(() => queryRequest?.size ?? DEFAULT_ROWS_PER_PAGE, [queryRequest?.size]);

  const rowsCount = useMemo(() => data?.totalCount, [data?.totalCount]);

  const pageChange = (event: GridPageChangeEvent) => {
    const { skip, take } = event.page;
    if (queryRequest && setQueryRequest) {
      const newQueryRequest: GearSearchQueryRequest = {
        ...queryRequest,
        skip,
        size: take
      };
      setQueryRequest(newQueryRequest);
    }
  };

  const [gridDimensions, setGridDimensions] = useState<ElementDimensions>({ width: 0, height: 0 });
  const windowDimensions = useWindowDimensions();

  useEffect(() => {
    if (ref && ref.current) {
      const paddingSize = 30;
      const height = document.body.offsetHeight - ref.current.getBoundingClientRect().top - paddingSize - 30;
      const width = ref.current.getBoundingClientRect().width - paddingSize;
      setGridDimensions({
        height,
        width
      });
    }
  }, [windowDimensions, selectedIndex]);

  useLayoutEffect(() => {
    if (ref && ref.current && gridData.length === 0) {
      const tableThread = ref.current.getElementsByClassName('k-table-thead')[0] as HTMLElement;
      const noRecordsWrapper = ref.current.getElementsByClassName('k-grid-table-wrap')[0] as HTMLElement;

      if (tableThread && noRecordsWrapper) {
        const tableThreadWidth = tableThread.getBoundingClientRect().width;
        noRecordsWrapper.style.width = `${tableThreadWidth}px`;
      }
    }
  }, [gridData.length]);

  const onSelectionChange = React.useCallback(
    (event: GridSelectionChangeEvent) => {
      // Check if user has selected text - if so, don't interfere with text selection
      // But allow shift-click multi-select to work (shift key means intentional row selection)
      const selection = window.getSelection();
      const isShiftClick = event.nativeEvent?.shiftKey;
      if (isShiftClick) {
        selection?.removeAllRanges();
      }
      if (selection && selection.toString().length > 0) {
        return;
      }

      const target = event.nativeEvent?.target as HTMLElement | null;

      if (target && target.closest('.linkCell, .linkedCell')) {
        return;
      }

      if (setSelectedDocuments) {
        const selectedIds = Object.keys(event.select).filter(id => event.select[id] === true);
        const selectedItems = event.dataItems.filter(item => selectedIds.includes(item[DOC_ID_FIELD]));
        setSelectedDocuments(selectedItems);
      }

      setSelect(event.select);
    },
    [setSelectedDocuments]
  );

  const onHeaderSelectionChange = React.useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      if (setSelectedDocuments) {
        const selectedIds = Object.keys(event.select).filter(id => event.select[id] === true);

        const selectedItems = event.dataItems.filter(item => selectedIds.includes(item[DOC_ID_FIELD]));

        setSelectedDocuments(selectedItems);
      }
      setSelect(event.select);
    },
    [setSelectedDocuments]
  );

  const removeGlobalFilter = (fieldToRemove: string, valueToRemove?: string) => {
    if (!queryRequest || !queryRequest.filters || !setQueryRequest) {
      return;
    }

    const updatedFilters = queryRequest.filters
      .map(filter => {
        if (filter.fieldName !== fieldToRemove || !filter.isGlobal) {
          return filter;
        }

        if (!valueToRemove) {
          return null;
        }

        const filterValues = filter.values;
        if (!filterValues || !Array.isArray(filterValues)) {
          return null;
        }

        const valuesArray = filterValues as Array<string | number | boolean>;
        const updatedValues = valuesArray.filter(v => v?.toString() !== valueToRemove);

        if (updatedValues.length === 0) {
          return null;
        }

        return {
          ...filter,
          values: updatedValues
        };
      })
      .filter((filter): filter is GearFilterRequest => filter !== null);

    setQueryRequest({
      ...queryRequest,
      filters: updatedFilters
    });
  };

  const removeActionsFilter = (valueToRemove?: string) => {
    if (!queryRequest || !setQueryRequest) {
      return;
    }

    if (!valueToRemove) {
      setQueryRequest({
        ...queryRequest,
        actionsFilter: undefined
      });
      return;
    }

    const updatedActionsFilter = queryRequest.actionsFilter?.filter(action => action.toString() !== valueToRemove);

    setQueryRequest({
      ...queryRequest,
      actionsFilter: updatedActionsFilter && updatedActionsFilter.length > 0 ? updatedActionsFilter : undefined
    });
  };

  const removeGridColumnFilter = (fieldToRemove: string, valueToRemove?: string) => {
    if (!compositeFilterDescriptor || !setCompositeFilterDescriptor) {
      return;
    }

    const newFilters = compositeFilterDescriptor.filters
      .map(descriptor => {
        const composite = descriptor as CompositeFilterDescriptor;
        if (composite.filters.length === 0) {
          return composite;
        }

        const firstFilter = composite.filters[0] as FilterDescriptor;
        if (firstFilter.field !== fieldToRemove) {
          return composite;
        }

        if (!valueToRemove) {
          return { ...composite, filters: [] };
        }

        const updatedFilters = composite.filters.filter(
          f => (f as FilterDescriptor).value?.toString() !== valueToRemove
        );

        return { ...composite, filters: updatedFilters };
      })
      .filter(d => (d as CompositeFilterDescriptor).filters.length > 0);

    setCompositeFilterDescriptor({
      logic: compositeFilterDescriptor.logic,
      filters: newFilters
    });
  };

  const removeFilter = (fieldToRemove: string, valueToRemove?: string) => {
    const isGlobalFilter = queryRequest?.filters?.some(f => f.fieldName === fieldToRemove && f.isGlobal);

    if (isGlobalFilter) {
      removeGlobalFilter(fieldToRemove, valueToRemove);
      return;
    }

    if (fieldToRemove === PERMISSIONS_FIELD) {
      removeActionsFilter(valueToRemove);
      return;
    }

    removeGridColumnFilter(fieldToRemove, valueToRemove);
  };

  const withFilterDescriptor = (filterDescriptor?: CompositeFilterDescriptor) => {
    return (props: GridCustomHeaderCellProps) => {
      return <HeaderCell {...props} compositeFilterDescriptor={filterDescriptor} />;
    };
  };

  const onColumnResize = (event: GridColumnResizeEvent) => {
    if (!event.end) {
      return;
    }

    const { index, newWidth, columns: eventColumns } = event;

    if (typeof index !== 'number' || typeof newWidth !== 'number' || !eventColumns) {
      return;
    }

    const columnAtIndex = eventColumns[index];

    if (!columnAtIndex || !columnAtIndex.field) {
      return;
    }

    const fieldName = columnAtIndex.field;

    setColumnChanges(prev => ({
      ...prev,
      widths: {
        ...prev.widths,
        [fieldName]: newWidth
      }
    }));
  };

  const onColumnReorder = (event: GridColumnReorderEvent) => {
    const { columns: eventColumns } = event;

    if (!eventColumns || !columns) {
      return;
    }

    const scrollContainer = ref.current?.querySelector('.k-grid-content');
    if (scrollContainer) {
      setScrollPosition(scrollContainer.scrollLeft);
    }

    const sortedColumns = [...eventColumns].sort((a, b) => (a as any).orderIndex - (b as any).orderIndex);

    const newOrder = sortedColumns.filter(col => col.field).map(col => col.field as string);

    setColumnChanges(prev => {
      return {
        ...prev,
        order: newOrder
      };
    });
  };

  const gridKey = columnChanges.order.join('-');

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const row = target.closest('tr[data-grid-row-index]');

    if (row && gridData.length > 0) {
      e.preventDefault();

      const rowIndex = parseInt(row.getAttribute('data-grid-row-index') || '0', 10);
      const clickedDocument = gridData[rowIndex];

      if (clickedDocument) {
        const docId = clickedDocument[DOC_ID_FIELD];

        if (!select?.[docId]) {
          const newSelect = { ...select, [docId]: true };
          setSelect(newSelect);

          if (setSelectedDocuments) {
            const selectedIds = Object.keys(newSelect).filter(id => newSelect[id] === true);
            const selectedItems = gridData.filter(item => selectedIds.includes(item[DOC_ID_FIELD]));
            setSelectedDocuments(selectedItems);
          }
        }

        setTimeout(() => {
          filterActionsHandleRef.current?.openContextMenu(e.clientX, e.clientY);
        }, 0);
      }
    }
  };

  return (
    <div ref={ref} className="gearGridContainer" onContextMenu={handleContextMenu}>
      {(isLoading || openDocumentLoading || currentViewLoading) && (
        <GearLoader
          type={openDocumentLoading ? 'pulsing' : 'infinite-spinner'}
          textMessageKey={openDocumentLoading ? 'custom.common.loader.opening' : undefined}
        />
      )}
      <Tooltip openDelay={500} anchorElement="target" parentTitle={true} position="bottom">
        <Grid
          key={gridKey}
          className="gearGrid"
          style={{ height: gridDimensions.height }}
          sortable={true}
          sort={sort}
          onSortChange={(event: GridSortChangeEvent) => sortChange(event.sort)}
          onFilterChange={(event: GridFilterChangeEvent) => {
            if (setCompositeFilterDescriptor) {
              setCompositeFilterDescriptor(event.filter);
            }
          }}
          reorderable={true}
          onColumnReorder={onColumnReorder}
          onColumnResize={onColumnResize}
          pageable={true}
          scrollable="scrollable"
          skip={queryRequest?.skip}
          take={pageSizeValue}
          total={rowsCount}
          onPageChange={pageChange}
          data={gridData}
          dataItemKey={DOC_ID_FIELD}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: 'multiple'
          }}
          select={select}
          onSelectionChange={onSelectionChange}
          onHeaderSelectionChange={onHeaderSelectionChange}
          navigatable={true}
          resizable={true}
          size={'small'}
          pager={pagerProps => (
            <GridPager
              {...pagerProps}
              columnChanges={columnChanges}
              columns={columns}
              onSaveComplete={() => {
                setColumnChanges(prev => ({
                  ...prev,
                  widths: {}
                }));
              }}
            />
          )}
          cells={{ data: cellRender, headerCell: withFilterDescriptor(compositeFilterDescriptor) }}
        >
          <GridNoRecords>
            <div className={queryRequest?.searchText ? 'noSearchResults' : 'noRecordsRow'}>
              {queryRequest?.searchText
                ? translate('grid.searchNoResults')
                : translate('custom.common.emptyGridMessage')}
            </div>
          </GridNoRecords>
          <GridToolbar className="gearToolbarContainer" nextButton={() => null} prevButton={() => null} overflow="none">
            <GearToolbar
              onFilterActionsMount={actions => {
                filterActionsHandleRef.current = actions;
              }}
              initialFacets={initialFacets}
              compositeFilterDescriptor={compositeFilterDescriptor}
              columns={columns}
              onRemoveFilter={removeFilter}
            />
          </GridToolbar>
          <GridColumn columnType="checkbox" reorderable={false} resizable={false} />
          {columnChanges.order
            .map(fieldName => columns?.find(col => col.name === fieldName))
            .filter((column): column is NonNullable<typeof column> => Boolean(column))
            .map(column => (
              <GridColumn
                key={column.id}
                field={column.name}
                title={column.displayName}
                sortable={column.sortable}
                filter={column.name === PERMISSIONS_FIELD ? undefined : GearFieldTypeToFilterType(column.type)}
                width={columnChanges.widths[column.name] ?? column.width}
                columnMenu={columnMenuProps => {
                  const fieldFacets = column.name === PERMISSIONS_FIELD ? actionsFacets : data?.facets?.[column.name];

                  return (
                    <ColumnMenu
                      {...columnMenuProps}
                      onSortChange={sortChange}
                      sortable={column.sortable}
                      filterable={column.filterable}
                      filter={compositeFilterDescriptor}
                      field={column.name}
                      fieldFacets={fieldFacets}
                      isNumericColumn={GearFieldTypeToFilterType(column.type) === GEAR_FIELD_TYPE.NUMERIC}
                      supportsBulkFilter={
                        column.type === GEAR_FIELD_TYPE.TEXT && (!fieldFacets || fieldFacets.length === 0)
                      }
                    />
                  );
                }}
              />
            ))}
        </Grid>
      </Tooltip>
    </div>
  );
};

export default GearGrid;
